import axios from 'axios';
import isUrl from 'is-url';
import joinUrl from 'proper-url-join';
import { removeSpace } from './utility';

import { checkIsTokenExpired, clearToken } from './utility';

class APIRequest {
  constructor(baseURL, httpsAgent, httpAgent, getToken) {
    if (!isUrl(baseURL)) throw new Error('The base URL provided is not valid');

    this.baseURL = baseURL;
    this.httpsAgent = httpsAgent;
    this.httpAgent = httpAgent;
    this.getToken = getToken;
  }

  async request(method, url, data) {
    let callURL = joinUrl(this.baseURL, url, { trailingSlash: false });

    const headers = {};
    const token = this.getToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token.get('accessToken')}`;
    }

    const cleanedData = removeSpace(data);

    if (['POST', 'PUT'].indexOf(method) !== -1) {
      headers['Content-Type'] = 'application/json';
    } else if (Object.keys(cleanedData).length && cleanedData.constructor === Object) {
      callURL = joinUrl(callURL, { trailingSlash: false, query: cleanedData });
    }

    return axios(callURL, {
      httpsAgent: this.httpsAgent,
      httpAgent: this.httpAgent,
      method,
      data: cleanedData,
      headers
    }).then(
      (response) => {
        if (response.status >= 400) {
          // check for 4XX, 5XX, wtv
          return Promise.reject(new Error(response.statusText));
        }

        if (response.status >= 200 && response.status <= 202) {
          return response.data;
        }

        return {};
      },
      async (error) => {
        const response = error.response;
        // If error 401 UNAUTHORIZED (invalid token) to refresh token & retry calling the API
        if (response.status === 401 && response.data === 'Access token is invalid') {
          const { data: refreshed } = await this.request('POST', '/auth/token', {
            refreshToken: token.get('refreshToken')
          });

          window.localStorage.setItem('id_token', refreshed.idToken);
          window.localStorage.setItem('access_token', refreshed.accessToken);
          window.localStorage.setItem('refresh_token', refreshed.refreshToken);

          headers['Authorization'] = `Bearer ${refreshed.accessToken}`;

          return axios(callURL, {
            httpsAgent: this.httpsAgent,
            httpAgent: this.httpAgent,
            method,
            data: cleanedData,
            headers
          }).then((response) => {
            if (response.status >= 400) {
              // check for 4XX, 5XX, wtv
              return Promise.reject(new Error(response.statusText));
            }

            if (response.status >= 200 && response.status <= 202) {
              return response.data;
            }

            return {};
          });
        }

        if (error.response) {
          return Promise.reject(error.response);
        }

        return Promise.reject(error);
      }
    );
  }

  async checkToken() {
    const token = this.getToken();
    const accessToken = token.get('accessToken');

    if (window.DEBUG_TOKEN) {
      console.log('----------');
      console.log('token', !!token);
      console.log('accessToken', !!accessToken);
    }

    if (!accessToken) {
      return;
    }

    const isExpired = checkIsTokenExpired(accessToken);

    if (isExpired) {
      console.log('Refreshing token...');

      try {
        const { data: refreshed } = await this.request('POST', '/auth/token', {
          refreshToken: token.get('refreshToken')
        });

        window.localStorage.setItem('id_token', refreshed.idToken);
        window.localStorage.setItem('access_token', refreshed.accessToken);
        window.localStorage.setItem('refresh_token', refreshed.refreshToken);

        console.log('Token refreshed successfully!');
      } catch (error) {
        console.log('Token fail to refresh!');
        clearToken();
        throw error;
      }
    }
  }

  async send(method, url, data = {}, checkToken = true) {
    if (checkToken) {
      await this.checkToken();
    }

    const response = await this.request(method, url, data);
    return response;
  }
}

class HSBB3 {
  constructor(options) {
    this.options = options;
    this.baseURL = options.baseURL;

    this.api = new APIRequest(options.baseURL, options.httpsAgent, options.httpAgent, options.getToken);
  }


  /* New NBC API start*/
  getUser = (id) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/user/${id}`);
  };

  verifyProfile = (body) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/profile-search`, body);
  };

  searchPostcode = (postcode) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/postcode-search?postcode=${postcode}`);
  };

  retrieveAddress = (postcode, keyword) => {
    return this.api.send('GET', `registrations/fibre-mass/nbc/address-search?postcode=${postcode}&locality=${keyword}`);
  };

  retrievePlans = (body) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/plans`, body);
  }

  retrieveAddOns = (packageId) => {
    return this.api.send('GET', `registrations/fibre-mass/nbc/addons?packageId=${packageId}`);
  };

  reserveVOBB = (session) => {
    return this.api.send('PUT', `/registrations/fibre-mass/nbc/${session}/reserve-user`);
  };

  submitNewRegistration = (session, data) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/${session}/submit-new-registration`, data);
  };

  submitApplication = (session, data) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/application/${session}/submit`, data);
  };  

  retrieveInstallSlots = (state, postCode, city, earliestStart, district, accessPartner, appointmentType) => {
    return this.api.send('GET', `registrations/fibre-mass/nbc/get-appointment-slots?state=${state}&postalCode=${postCode}&startDateTime=${earliestStart}&district=${district}&accessPartner=${accessPartner}&appointmentType=${appointmentType}&city=${city}`);
  };

  checkEligibility = (body) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/check-eligibility`, body);
  };

  checkSubscriberEligibility = (body) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/check-subscriber-eligibility`, body);
  };

  confirmAppointment = (session, data) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/${session}/confirm-appointment`, data);
  };

  rescheduleEligibilityCheck = (key_id) => {
    return this.api.send('PUT', `/registrations/fibre-mass/nbc/${key_id}/reschedule-eligibility-check`);
  };

  rescheduleAppointment = (session, data) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/${session}/reschedule-appointment`, data);
  };
 accountDetails = (vobbNumber) => {
  return this.api.send('GET', `/registrations/fibre-mass/nbc/account-details?vobbNumber=${vobbNumber}`);
 };

  // NBC troubleshooitng
  searchTrList = (params) => {
    const queryString = `?custType=${params.custType}&custID=${params.custID}`
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte2/search-customer-services${queryString}`);
  };

  getTrDetails = (params) => {
    let queryString = `?vobbNumber=${params.vobbNumber}`
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte2/trouble-shooting-details${queryString}`);
  };

  // Bau troubleshooting
  searchTrCelcomList = (params) => {
    const queryString = `?custType=${params.custType}&custID=${params.custID}`
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte/search-customer-services${queryString}`);
  };

  getTrCelcomDetails = (params) => {
    let queryString = `?serialNumber=${params.serialNumber}&subscriberId=${params.subscriberId}&fetchAddressDetail=${params.fetchAddressDetail}`
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte/trouble-shooting-details${queryString}`);
  };

  getTrcelcomPartnerDetails = (msisdn) => {
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte/partner-details?msisdn=${msisdn}`);
  };

  getTrCelcomContractDetails = (accountNO) => {
    return this.api.send('GET', `/registrations/fibre-mass/ts/dte/contract-details?billingProfileId=${accountNO}`);
  };

  searchCelcomService = (params) => {
    // Dummy API for Celcom Service search
    return Promise.resolve({
      data: {
        customerInfo: {
          services: [],
          salutation: 'Dummy',
          name: 'Dummy Customer',
          id_number: 'Dummy ID',
          id_type: 'Dummy Type'
        }
      }
    });
  };

  getSearchPartnerInfo = (params={}) => {
    let query = "";
    if(params?.searchKey && params?.searchValue){
      query = `?searchKey=${params.searchKey}&searchValue=${params.searchValue}`
    }
    return this.api.send('GET', `/registrations/fibre-mass/nbc/partner-search${query}`);
  };

getPartnerInfo = (id) => {
  return this.api.send('GET', `/registrations/fibre-mass/nbc/partner-info/${id}`);
};



  /* New NBC API End*/




  /* Common APIs Start*/
  getApplication = (session) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/application/${session}`);
  };

  updateCelcomFibreFlagMessage = (session, body) => {
    return this.api.send('PUT', `/registrations/fibre-mass/nbc/${session}/celcom-flag-message`, body)
  }

  updateCreatorID = (session, data) => {
    return this.api.send('POST', `/registrations/fibre-mass/nbc/${session}/update-creator`, data);
  };

  getPromotions = () => {
    return this.api.send('GET', `/promotion?is_active=true&date=today`);
  };  

  /* Common APIs End*/




  /* FPFP APIs for application related start*/
  checkOpenOrderCount = (id, serviceType) => {
    return this.api.send('GET', `registrations/fibre-mass/fpfp/open-order-count?customerId=${id}&serviceType=${serviceType}`);
  };

  retrieveServiceDetails = (Id, Number) => {
    return this.api.send('GET', `/registrations/fibre-mass/nbc/address-details-retrieve?vobbNumber=${Id}&subscriberId=${Number}`);
  };
  
  updatePartnerReferenceId = (id, body) => {
    return this.api.send('PUT', `/registrations/fibre-mass/${id}/partner-reference`, body);
  };

  // retrieveInstallSlots = (state, postCode, activityId, earliestStart, district, incumbent, appointmentType) => {
  //   return this.api.send('GET', `registrations/fibre-mass/fpfp/retrieve-installer-slots?state=${state}&activityId=${activityId}&postCode=${postCode}&earliestStart=${earliestStart}&district=${district}&incumbent=${incumbent}&appointmentType=${appointmentType}`);
  // };

  createFibreMass = (fibreMass) => {
    return this.api.send('POST', `/registrations/fibre-mass`, fibreMass);
  };

  updateFibreMassAccountCode = (id, body) => {
    return this.api.send('PUT', `registrations/fibre-mass/${id}/account-code`, body);
  };

  updateFibreMass = (changes, key_id) => {
    return this.api.send('PUT', `/registrations/fibre-mass/${key_id}`, changes);
  };

  // checkBlacklist = (body) => {
  //   return this.api.send('POST', `/registrations/fibre-mass/fpfp/blacklist`, body);
  // };

  updateModifiedStatusToTask = (body) => {
    return this.api.send('PUT', `/user-group/modified`, body)
  }

  cancelFiberMass = (key_id, body) => {
    return this.api.send('PUT', `/registrations/fibre-mass/fpfp/${key_id}/cancel`, body);
  };
cancelProductOrderNBC = (key_id, body) => {
  return this.api.send('PUT', `/registrations/fibre-mass/nbc/${key_id}/cancel`, body);
};
  approveFibreMassReleaseLetter = (id) => {
    return this.api.send('PUT', `/registrations/fibre-mass/${id}/release-letter/approval`);
  };

  cancelAppointment = (key_id, body) => {
    return this.api.send('POST', `/registrations/fibre-mass/fpfp/${key_id}/cancel-appointment`, body);
  }; 

  /* FPFP APIs for application related end*/

}

export default HSBB3;
